import React, { useState, useMemo } from 'react';
import { SlideOver } from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { mockMenuItems, getCachedCategorySortOrder } from '../../data/mockMenuItems';
import type { MenuItem } from '../../data/types';

interface CategoryItemPickerProps {
  mode: 'category' | 'item';
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  menuItems?: MenuItem[];
  /** When true, only one item can be selected at a time */
  singleSelect?: boolean;
  /** Override the field label (default: "Categories" or "Items") */
  label?: string;
  /** Item IDs to hide from the picker (e.g. items already in the other list) */
  excludeIds?: string[];
  /** Show the required asterisk next to the label (default: true) */
  required?: boolean;
  /** Inline validation error message shown below the field */
  error?: string;
}

/** Derive unique categories with item counts from menu data */
function getCategories(menuItems: MenuItem[]) {
  const map = new Map<string, number>();
  for (const item of menuItems) {
    const cats = item.categories && item.categories.length > 0
      ? item.categories.map((c) => c.name)
      : [item.category];
    cats.forEach((catName) => {
      const normalized = String(catName || '').trim();
      if (!normalized || normalized.toLowerCase() === 'uncategorized') return;
      map.set(normalized, (map.get(normalized) || 0) + 1);
    });
  }
  return Array.from(map.entries()).map(([name, count]) => ({ name, count }));
}

/** Group items by category, sorted by the category's sortOrder from the API */
function getItemsByCategory(menuItems: MenuItem[]) {
  const groups: Record<string, { id: string; name: string; price: number }[]> = {};
  const categorySortOrder: Record<string, number> = {};
  const globalSortOrderCache = getCachedCategorySortOrder();

  for (const item of menuItems) {
    const cats = item.categories && item.categories.length > 0
      ? item.categories
      : [{ name: item.category, sortOrder: undefined as number | undefined }];

    cats.forEach((cat) => {
      const normalized = String(cat.name || '').trim();
      if (!normalized || normalized.toLowerCase() === 'uncategorized') return;
      if (!groups[normalized]) groups[normalized] = [];
      if (!groups[normalized].some((x) => x.id === item.id)) {
        groups[normalized].push({ id: item.id, name: item.name, price: item.price });
      }

      const resolvedOrder =
        cat.sortOrder != null
          ? cat.sortOrder
          : globalSortOrderCache[normalized.toLowerCase()] ?? undefined;

      if (resolvedOrder != null) {
        if (categorySortOrder[normalized] == null || resolvedOrder < categorySortOrder[normalized]) {
          categorySortOrder[normalized] = resolvedOrder;
        }
      }
    });
  }

  const sortedGroups: Record<string, { id: string; name: string; price: number }[]> = {};
  Object.keys(groups)
    .sort((a, b) => {
      const oa = categorySortOrder[a] ?? Infinity;
      const ob = categorySortOrder[b] ?? Infinity;
      if (oa !== ob) return oa - ob;
      return a.localeCompare(b);
    })
    .forEach((cat) => { sortedGroups[cat] = groups[cat]; });

  return sortedGroups;
}

const CategoryItemPicker: React.FC<CategoryItemPickerProps> = ({ mode, selectedIds, onChange, menuItems = mockMenuItems, singleSelect, label: labelProp, excludeIds = [], required = true, error }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const categories = useMemo(() => getCategories(menuItems), [menuItems]);
  const itemsByCategory = useMemo(() => getItemsByCategory(menuItems), [menuItems]);

  const handleOpen = () => {
    setDraft([...selectedIds]);
    setSearch('');
    setOpen(true);
  };

  const handleDone = () => {
    onChange(draft);
    setOpen(false);
  };

  const toggleDraft = (id: string) => {
    if (singleSelect) {
      setDraft((prev) => prev.includes(id) ? [] : [id]);
    } else {
      setDraft((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    }
  };

  const removeChip = (id: string) => {
    onChange(selectedIds.filter((x) => x !== id));
  };

  const filteredByCategory = useMemo(() => {
    if (mode !== 'item') return itemsByCategory;
    const q = search.trim().toLowerCase();
    const result: Record<string, { id: string; name: string; price: number }[]> = {};
    for (const [cat, items] of Object.entries(itemsByCategory)) {
      const filtered = items.filter((i) => {
        if (excludeIds.includes(i.id)) return false;
        if (q && !i.name.toLowerCase().includes(q) && !cat.toLowerCase().includes(q)) return false;
        return true;
      });
      if (filtered.length > 0) result[cat] = filtered;
    }
    return result;
  }, [mode, search, itemsByCategory, excludeIds]);

  const defaultLabel = mode === 'category' ? 'Categories' : 'Items';
  const label = labelProp || defaultLabel;
  const singleLabel = mode === 'category' ? 'Category' : 'Item';

  const triggerText = singleSelect
    ? (selectedIds.length > 0 ? `Change ${singleLabel}` : `Select ${singleLabel}`)
    : (selectedIds.length > 0 ? `Add More ${defaultLabel}` : `Add ${defaultLabel}`);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
      <span style={styles.label}>
        {label}
        {required && <span style={{ color: 'var(--accent, #D23A3B)', marginLeft: 2 }}>*</span>}
      </span>

      {/* Selected chips */}
      {selectedIds.length > 0 && (
        <div style={styles.chipRow}>
          {selectedIds.map((id) => {
            const name = menuItems.find((m) => m.id === id)?.name ?? id;
            return (
              <span key={id} style={styles.chip}>
                {name}
                <button
                  type="button"
                  onClick={() => removeChip(id)}
                  style={styles.chipX}
                  aria-label={`Remove ${name}`}
                >
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2.5 2.5L7.5 7.5M7.5 2.5L2.5 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Trigger button */}
      <button
        type="button"
        onClick={handleOpen}
        style={{ ...styles.addBtn, ...(error ? { borderColor: 'var(--red, #E5484D)', borderStyle: 'solid' } : {}) }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
        {triggerText}
      </button>

      {error && <p style={styles.errorText}>{error}</p>}

      {/* SlideOver drawer */}
      <SlideOver
        open={open}
        onClose={() => setOpen(false)}
        title={singleSelect ? `Select ${singleLabel}` : `Select ${defaultLabel}`}
        width={380}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleDone} disabled={singleSelect && draft.length === 0}>
              {singleSelect
                ? (draft.length > 0 ? `Select "${menuItems.find((m) => m.id === draft[0])?.name ?? draft[0]}"` : 'Select')
                : `Done${draft.length > 0 ? ` (${draft.length})` : ''}`
              }
            </Button>
          </>
        }
      >
        {mode === 'category' ? (
          /* ── Category list ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {categories.map((cat) => {
              const checked = draft.includes(cat.name);
              return (
                <div
                  key={cat.name}
                  onClick={() => toggleDraft(cat.name)}
                  style={{
                    ...styles.optionRow,
                    borderColor: checked ? 'var(--accent)' : 'var(--card-border)',
                    background: checked ? 'var(--accent-soft)' : 'var(--surface)',
                  }}
                >
                  <Checkbox checked={checked} />
                  <div style={{ flex: 1 }}>
                    <span style={styles.optionName}>{cat.name}</span>
                    <span style={styles.optionMeta}>{cat.count} item{cat.count !== 1 ? 's' : ''}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ── Item list with search ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Search */}
            <div style={styles.searchWrap}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: 'var(--t3)' }}>
                <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M11 11L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search items..."
                style={styles.searchInput}
              />
              {search && (
                <button type="button" onClick={() => setSearch('')} style={styles.searchClear} aria-label="Clear">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>

            {/* Grouped items */}
            {Object.entries(filteredByCategory).map(([cat, items]) => {
              const allItemIds = items.map((i) => i.id);
              const allChecked = allItemIds.every((id) => draft.includes(id));
              const someChecked = !allChecked && allItemIds.some((id) => draft.includes(id));
              const toggleCategory = () => {
                if (singleSelect) return;
                setDraft((prev) => {
                  if (allChecked) return prev.filter((id) => !allItemIds.includes(id));
                  return [...prev, ...allItemIds.filter((id) => !prev.includes(id))];
                });
              };
              return (
              <div key={cat}>
                <div
                  onClick={toggleCategory}
                  style={{ ...styles.categoryRow, cursor: singleSelect ? 'default' : 'pointer' }}
                >
                  {!singleSelect && <Checkbox checked={allChecked} indeterminate={someChecked} />}
                  <span style={styles.groupLabel2}>{cat}</span>
                  <span style={styles.categoryCount}>{allItemIds.length} item{allItemIds.length !== 1 ? 's' : ''}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {items.map((item) => {
                    const checked = draft.includes(item.id);
                    return (
                      <div
                        key={item.id}
                        onClick={() => toggleDraft(item.id)}
                        style={{
                          ...styles.optionRow,
                          padding: '8px 12px',
                          borderColor: checked ? 'var(--accent)' : 'var(--card-border)',
                          background: checked ? 'var(--accent-soft)' : 'var(--surface)',
                        }}
                      >
                        {singleSelect ? <Radio checked={checked} /> : <Checkbox checked={checked} />}
                        <span style={{ ...styles.optionName, flex: 1 }}>{item.name}</span>
                        <span style={styles.price}>${item.price.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })}

            {Object.keys(filteredByCategory).length === 0 && (
              <p style={{ fontSize: 13, color: 'var(--t3)', textAlign: 'center', padding: '24px 0', margin: 0 }}>
                No items match "{search}"
              </p>
            )}
          </div>
        )}
      </SlideOver>
    </div>
  );
};

/** Inline radio circle (presentational, for singleSelect) */
function Radio({ checked }: { checked: boolean }) {
  return (
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        border: checked ? '2px solid var(--accent)' : '2px solid var(--card-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      {checked && (
        <div style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: 'var(--accent)',
        }} />
      )}
    </div>
  );
}

/** Inline checkbox square (presentational) */
function Checkbox({ checked, indeterminate }: { checked: boolean; indeterminate?: boolean }) {
  const filled = checked || indeterminate;
  return (
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: 4,
        border: filled ? 'none' : '2px solid var(--card-border)',
        backgroundColor: filled ? 'var(--accent)' : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {indeterminate && (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 6H9" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--t2, #5A6178)',
    fontFamily: 'var(--font-primary)',
    lineHeight: '16px',
  },
  errorText: {
    fontSize: 11,
    fontWeight: 500,
    color: 'var(--red, #E5484D)',
    fontFamily: 'var(--font-primary)',
    margin: 0,
    marginTop: 2,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '4px 8px 4px 10px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 500,
    fontFamily: 'var(--font-primary)',
    color: 'var(--accent)',
    background: 'var(--accent-soft)',
    border: '1px solid rgba(210,58,59,0.15)',
    whiteSpace: 'nowrap',
  },
  chipX: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    color: 'var(--accent)',
    opacity: 0.6,
  },
  addBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 8,
    border: '1.5px dashed var(--card-border)',
    background: 'transparent',
    color: 'var(--t2)',
    fontSize: 12.5,
    fontWeight: 500,
    fontFamily: 'var(--font-primary)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    alignSelf: 'flex-start',
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid var(--card-border)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  optionName: {
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--t1)',
    fontFamily: 'var(--font-primary)',
  },
  optionMeta: {
    fontSize: 11,
    color: 'var(--t3)',
    fontFamily: 'var(--font-primary)',
    marginLeft: 8,
  },
  price: {
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--t3)',
    fontFamily: 'var(--font-mono)',
  },
  groupLabel: {
    fontSize: 10.5,
    fontWeight: 600,
    color: 'var(--t3)',
    fontFamily: 'var(--font-primary)',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.8,
    margin: '0 0 6px',
  },
  categoryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 4px 6px 0',
    marginBottom: 4,
    cursor: 'pointer',
    borderBottom: '1px solid var(--divider, #E2E5EC)',
  },
  groupLabel2: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--t2)',
    fontFamily: 'var(--font-primary)',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.8,
    flex: 1,
  },
  categoryCount: {
    fontSize: 11,
    color: 'var(--t3)',
    fontFamily: 'var(--font-primary)',
  },
  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    borderRadius: 20,
    border: '1px solid var(--card-border)',
    background: 'var(--hover)',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: 13,
    fontFamily: 'var(--font-primary)',
    color: 'var(--t1)',
    padding: 0,
  },
  searchClear: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    color: 'var(--t3)',
  },
};

export default CategoryItemPicker;
